<template>
  <div class="wmap">
    <svg v-if="ready && shapes.length"
         :viewBox="viewBox"
         class="wmap-svg"
         preserveAspectRatio="xMidYMid meet"
         @pointerdown.prevent="panStart"
         @wheel.prevent="onWheel"
         @mouseleave="onLeaveCountry">
      <path v-for="s in orderedShapes"
            :key="s.name"
            :d="s.d"
            :fill="s.fill"
            class="wmap-country"
            :class="{ 'wmap-clickable': s.present, 'wmap-selected': s.selected }"
            :style="{ stroke: s.selected ? '#111111' : (s.present ? '#1a1a1a' : '#ffffff'), strokeWidth: s.selected ? '2.6' : (s.present ? '0.7' : '0.4'), opacity: s.opacity }"
            @click="onCountryClick(s)"
            @mouseenter="onHoverCountry(s)">
        <title>{{ (s.present ? (s.selected ? '✓ filtering by ' : 'Click to filter by ') : '') + s.title }}</title>
      </path>
    </svg>
    <div v-else class="wmap-empty">No country data for the current filters.</div>

    <div class="wmap-zoom" v-if="ready && shapes.length">
      <button type="button" @click="zoomOut" title="Zoom out" aria-label="Zoom out">&minus;</button>
      <button type="button" @click="zoomIn" title="Zoom in" aria-label="Zoom in">+</button>
    </div>
  </div>
</template>

<script>
import { geoNaturalEarth1, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import topo from "@/assets/world.countries.topo.json";

// Data country (already UPPER-normalized by the backend) -> the world-atlas country name (UPPER).
// Most match directly on UPPER name; only a few differ.
const NAME_ALIAS = {
  "UNITED STATES": "UNITED STATES OF AMERICA",
  "USA": "UNITED STATES OF AMERICA",
  "KOREA": "SOUTH KOREA",
  "SOUTH KOREA": "SOUTH KOREA",
  "RUSSIA": "RUSSIA",
};

export default {
  name: "WorldMap",
  props: {
    // [{ country: 'AUSTRALIA', orderCount, customers, sales }] — normalized, from the report's byCountry.
    byCountry: { type: Array, default: () => [] },
    // Base highlight color for countries that have accounts (the International territory color).
    color: { type: String, default: "#2E86DE" },
    // Selected country names (the report's filters.country, UPPER) — bold-outlined on the map.
    selected: { type: Array, default: () => [] },
    zoom: { type: Number, default: 1 },
  },
  data() {
    return { width: 760, height: 388, ready: false, shapes: [], panX: 0, panY: 0 };
  },
  computed: {
    // world-atlas name (UPPER) -> aggregate row, so we can look up each rendered country.
    present() {
      const m = {};
      (this.byCountry || []).forEach((c) => {
        if (!c || !c.country) return;
        const up = String(c.country).toUpperCase();
        m[NAME_ALIAS[up] || up] = c;
      });
      return m;
    },
    maxSales() {
      return (this.byCountry || []).reduce((mx, c) => Math.max(mx, Number(c.sales) || 0), 0);
    },
    viewBox() {
      var z = this.zoom > 0 ? this.zoom : 1;
      var w = this.width / z, h = this.height / z;
      var x = (this.width - w) / 2 + (this.panX || 0), y = (this.height - h) / 2 + (this.panY || 0);
      return x.toFixed(1) + " " + y.toFixed(1) + " " + w.toFixed(1) + " " + h.toFixed(1);
    },
    // Selected countries render LAST so their bold black border isn't overdrawn by a neighbor's fill —
    // mirrors the US map's orderedShapes; this is what actually makes the black highlight visible.
    orderedShapes() {
      if (!this.shapes.some((s) => s.selected)) return this.shapes;
      return this.shapes.slice().sort(function (a, b) { return (a.selected ? 1 : 0) - (b.selected ? 1 : 0); });
    },
  },
  watch: {
    byCountry: { deep: true, handler() { this.recolor(); } },
    color() { this.recolor(); },
    selected: { deep: true, handler() { this.recolor(); } },
  },
  mounted() { this.build(); },
  methods: {
    // Project the world topojson once into SVG path strings (geometry never changes).
    build() {
      try {
        const fc = feature(topo, topo.objects.countries);
        const projection = geoNaturalEarth1().fitSize([this.width, this.height], fc);
        const path = geoPath(projection);
        this._features = (fc.features || []).map((ft) => ({
          name: (ft.properties && ft.properties.name) || "",
          d: path(ft),
        }));
        this.ready = true;
        this.recolor();
      } catch (e) {
        this.ready = false;
      }
    },
    // Countries with accounts get the highlight color, shaded by sales (opacity); the rest stay light gray.
    recolor() {
      if (!this._features) return;
      const max = this.maxSales;
      const fmtMoney = (n) => "$" + (Number(n) || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
      this.shapes = this._features.map((f) => {
        const rec = this.present[(f.name || "").toUpperCase()];
        const op = rec && max > 0 ? (0.45 + 0.55 * ((Number(rec.sales) || 0) / max)) : 1;
        return {
          name: f.name,
          d: f.d,
          present: !!rec,
          countryVal: rec ? rec.country : null,
          selected: !!(rec && (this.selected || []).indexOf(rec.country) >= 0),
          fill: rec ? this.color : "#e9e9ec",
          opacity: rec ? op : 1,
          sales: rec ? (Number(rec.sales) || 0) : 0,
          customers: rec ? (Number(rec.customers) || 0) : 0,
          orders: rec ? (Number(rec.orderCount) || 0) : 0,
          title: rec
            ? f.name + " — " + fmtMoney(rec.sales) + " · " + (Number(rec.customers) || 0) + " customers · " + (Number(rec.orderCount) || 0) + " orders"
            : f.name,
        };
      });
    },
    zoomIn() { this.emitZoom(this.zoom * 1.25); },
    zoomOut() { this.emitZoom(this.zoom / 1.25); },
    // Mouse-wheel zoom (in = scroll up). preventDefault on the <svg> stops the page scrolling under it.
    onWheel(e) {
      var f = (e.deltaY < 0) ? 1.15 : 1 / 1.15;
      this.emitZoom((this.zoom > 0 ? this.zoom : 1) * f);
    },
    emitZoom(z) {
      var c = Math.min(6, Math.max(0.6, Math.round(z * 100) / 100));
      this.$emit("zoom-change", c);
    },
    // Hovering a country surfaces its flag + stats in the parent's panel below the world map.
    onHoverCountry(s) { this.$emit("hover-country", { name: s.name, present: s.present, sales: s.sales, customers: s.customers, orders: s.orders }); },
    onLeaveCountry() { this.$emit("hover-country", null); },
    // Click a country (that has accounts) to filter the report by it — unless the pointer was dragging.
    onCountryClick(s) {
      if (this._justDragged) return;
      if (!s || !s.countryVal) return;
      // Clicking the US drills into the US states map instead of adding a country filter.
      if (String(s.countryVal).toUpperCase() === "UNITED STATES") { this.$emit("show-us"); return; }
      this.$emit("select-country", s.countryVal);
    },
    // Drag-to-pan: offset the viewBox by the pointer delta (screen px -> viewBox units).
    panStart(e) {
      this._pan = { sx: e.clientX, sy: e.clientY, px: this.panX, py: this.panY, w: (e.currentTarget && e.currentTarget.clientWidth) || this.width };
      this._moved = false;
      window.addEventListener("pointermove", this.panMove);
      window.addEventListener("pointerup", this.panEnd);
    },
    panMove(e) {
      if (!this._pan) return;
      var dx = e.clientX - this._pan.sx, dy = e.clientY - this._pan.sy;
      if (Math.abs(dx) + Math.abs(dy) > 3) this._moved = true;
      var z = this.zoom > 0 ? this.zoom : 1;
      var scale = (this.width / z) / (this._pan.w || this.width);
      this.panX = this._pan.px - dx * scale;
      this.panY = this._pan.py - dy * scale;
    },
    panEnd() {
      this._pan = null;
      window.removeEventListener("pointermove", this.panMove);
      window.removeEventListener("pointerup", this.panEnd);
      if (this._moved) { var self = this; this._justDragged = true; setTimeout(function () { self._justDragged = false; }, 60); }
    },
  },
  beforeDestroy() {
    window.removeEventListener("pointermove", this.panMove);
    window.removeEventListener("pointerup", this.panEnd);
  },
};
</script>

<style scoped>
.wmap { width: 100%; position: relative; }
.wmap-svg { width: 100%; height: auto; display: block; cursor: grab; touch-action: none; }
.wmap-svg:active { cursor: grabbing; }
.wmap-country { transition: opacity 0.15s; }
.wmap-country:hover { opacity: 0.82 !important; }
.wmap-clickable { cursor: pointer; }
.wmap-zoom { position: absolute; top: 6px; right: 6px; display: flex; flex-direction: column; gap: 4px; z-index: 2; }
.wmap-zoom button { width: 24px; height: 24px; border: 1px solid #d4d4d8; background: rgba(255,255,255,.92); color: #555; font-size: 16px; font-weight: 700; line-height: 1; border-radius: 4px; cursor: pointer; padding: 0; }
.wmap-zoom button:hover { background: #9e2436; color: #fff; border-color: #9e2436; }
.wmap-empty { padding: 30px; text-align: center; color: #999; font-size: 13px; }
</style>
